/**
 * Hardened File Upload Utilities
 * 
 * Provides resilient file upload with:
 * - Retry logic for network hiccups
 * - Progress tracking with XHR
 * - iOS Safari compatibility (HEIC conversion)
 * - MIME type validation
 * - Size limit enforcement
 * - Image compression using browser-image-compression
 */

import imageCompression from 'browser-image-compression';

// ===================== FILE TYPE CONFIGURATIONS =====================

// Supported image types
export const IMAGE_TYPES = {
  extensions: ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'],
  mimeTypes: [
    'image/jpeg', 
    'image/jpg', 
    'image/png', 
    'image/webp',
    'image/heic',
    'image/heif'
  ],
  maxSizeMB: 10,
  displayName: 'JPG, PNG, or WebP',
  maxDimension: 2048, // Max width/height for compression
  compressionQuality: 0.85
};

// Supported audio types
export const AUDIO_TYPES = {
  extensions: ['.mp3', '.wav'],
  mimeTypes: ['audio/mpeg', 'audio/wav', 'audio/x-wav', 'audio/wave', 'audio/mp3'],
  maxSizeMB: 100,
  displayName: 'MP3 or WAV'
};

// Supported video types
export const VIDEO_TYPES = {
  extensions: ['.mp4', '.mov', '.webm'],
  mimeTypes: ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v'],
  maxSizeMB: 500,
  displayName: 'MP4, MOV, or WebM'
};

// ===================== ERROR MESSAGES =====================

export const UPLOAD_ERRORS = {
  NO_FILE: 'No file selected',
  INVALID_TYPE: (allowed) => `Unsupported file type. Please use ${allowed}.`,
  FILE_TOO_LARGE: (size, max) => `File too large (${size}MB). Maximum size: ${max}MB`,
  FILE_EMPTY: 'File is empty. Please select a valid file.',
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  UPLOAD_PAUSED: 'Upload paused. Tap retry to continue.',
  SERVER_ERROR: 'Server error. Please try again later.',
  AUTH_ERROR: 'Authentication error. Please log in and try again.',
  PERMISSION_ERROR: 'You don\'t have permission to upload this type of file.',
  COMPRESSION_FAILED: 'Failed to process image. Please try a different file.',
  TIMEOUT: 'Upload timed out. Please try again with a smaller file or better connection.'
};

// ===================== VALIDATION =====================

/**
 * Validate a file before upload
 * @param {File} file - The file to validate
 * @param {Object} fileTypes - Configuration for allowed types
 * @returns {{ valid: boolean, error: string | null }}
 */
export function validateFile(file, fileTypes) {
  if (!file) {
    return { valid: false, error: UPLOAD_ERRORS.NO_FILE };
  }

  // Check file extension
  const fileName = file.name.toLowerCase();
  const extension = '.' + fileName.split('.').pop();
  const hasValidExtension = fileTypes.extensions.includes(extension);

  // Check MIME type (allow empty for iOS quirks)
  const hasValidMime = fileTypes.mimeTypes.includes(file.type) || file.type === '' || file.type === 'application/octet-stream';

  if (!hasValidExtension && !hasValidMime) {
    return { 
      valid: false, 
      error: UPLOAD_ERRORS.INVALID_TYPE(fileTypes.displayName)
    };
  }

  // Check file size
  const maxBytes = (fileTypes.maxSizeMB || 10) * 1024 * 1024;
  if (file.size > maxBytes) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return { 
      valid: false, 
      error: UPLOAD_ERRORS.FILE_TOO_LARGE(sizeMB, fileTypes.maxSizeMB)
    };
  }

  // Check for empty file
  if (file.size === 0) {
    return { valid: false, error: UPLOAD_ERRORS.FILE_EMPTY };
  }

  return { valid: true, error: null };
}

/**
 * Validate image file specifically
 */
export function validateImageFile(file) {
  return validateFile(file, IMAGE_TYPES);
}

// ===================== IMAGE COMPRESSION =====================

/**
 * Compress an image file to reduce size using browser-image-compression
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File>} - Compressed image file
 */
export async function compressImage(file, options = {}) {
  const {
    maxDimension = IMAGE_TYPES.maxDimension,
    quality = IMAGE_TYPES.compressionQuality
  } = options;

  const compressionOptions = {
    maxSizeMB: 2, // Target output size
    maxWidthOrHeight: maxDimension,
    useWebWorker: true,
    initialQuality: quality,
    fileType: 'image/jpeg',
    alwaysKeepResolution: false
  };

  try {
    console.log(`[Compress] Starting compression for ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)...`);
    const compressedFile = await imageCompression(file, compressionOptions);
    console.log(`[Compress] ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
    return compressedFile;
  } catch (err) {
    console.error('[Compress] Compression failed:', err);
    // Return original file if compression fails
    return file;
  }
}

/**
 * Process image file - compress if needed, convert HEIC
 * @param {File} file - The image file
 * @returns {Promise<{ file: File | Blob, wasCompressed: boolean }>}
 */
export async function processImageFile(file) {
  // Check if HEIC/HEIF (needs conversion)
  const isHEIC = file.type === 'image/heic' || 
                 file.type === 'image/heif' || 
                 file.name.toLowerCase().endsWith('.heic') ||
                 file.name.toLowerCase().endsWith('.heif');

  // Check if compression needed (> 2MB or HEIC)
  const needsCompression = file.size > 2 * 1024 * 1024 || isHEIC;

  if (!needsCompression) {
    return { file, wasCompressed: false };
  }

  try {
    console.log(`[ProcessImage] Compressing ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)...`);
    
    const compressedFile = await compressImage(file, {
      maxDimension: IMAGE_TYPES.maxDimension,
      quality: IMAGE_TYPES.compressionQuality
    });

    return { file: compressedFile, wasCompressed: true };
  } catch (err) {
    console.error('[ProcessImage] Compression failed:', err);
    // Return original file if compression fails
    return { file, wasCompressed: false };
  }
}

// ===================== UPLOAD WITH RETRY =====================

/**
 * Parse error response from server
 * Handles both string errors and structured JSON error objects
 */
function parseServerError(responseText, status) {
  try {
    const errorData = JSON.parse(responseText);
    // Handle structured error response
    if (typeof errorData.detail === 'object') {
      return {
        message: errorData.detail.message || `Upload failed (${status})`,
        code: errorData.detail.error_code || null,
        maxSize: errorData.detail.max_size_mb,
        actualSize: errorData.detail.actual_size_mb
      };
    }
    // Handle string detail
    return {
      message: errorData.detail || errorData.message || errorData.error || `Upload failed (${status})`,
      code: null
    };
  } catch {
    return { message: `Upload failed (${status})`, code: null };
  }
}

/**
 * Upload a file with retry logic and progress tracking
 * @param {Object} options - Upload options
 * @returns {Promise<{ success: boolean, data?: any, error?: string, canRetry?: boolean }>}
 */
export async function uploadFileWithRetry({
  file,
  url,
  headers = {},
  onProgress = () => {},
  onStatus = () => {},
  maxRetries = 3,
  retryDelay = 1000,
  fieldName = 'file'
}) {
  let lastError = null;
  let lastStatus = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[Upload] Attempt ${attempt}/${maxRetries} for ${file.name}`);
      onStatus(`Uploading... (attempt ${attempt})`);
      
      const result = await uploadWithProgress(file, url, headers, onProgress, fieldName);
      
      if (result.success) {
        console.log(`[Upload] Success on attempt ${attempt}`);
        onStatus('Upload complete');
        return { ...result, canRetry: false };
      }
      
      lastError = result.error;
      lastStatus = result.status;
      
      // Don't retry on client errors (4xx) - these are validation failures
      if (result.status && result.status >= 400 && result.status < 500) {
        console.log(`[Upload] Client error ${result.status}, not retrying`);
        
        // Map status codes to user-friendly messages
        let userError = result.error;
        if (result.status === 413) {
          // File too large - use server message or fallback
          userError = result.errorDetails?.message || 
            UPLOAD_ERRORS.FILE_TOO_LARGE((file.size / 1024 / 1024).toFixed(1), IMAGE_TYPES.maxSizeMB);
        } else if (result.status === 415) {
          // Unsupported media type - use server message or fallback
          userError = result.errorDetails?.message || 
            UPLOAD_ERRORS.INVALID_TYPE(IMAGE_TYPES.displayName);
        } else if (result.status === 401) {
          userError = result.errorDetails?.message || UPLOAD_ERRORS.AUTH_ERROR;
        } else if (result.status === 403) {
          // Permission/tier error - use server message which includes tier details
          userError = result.errorDetails?.message || UPLOAD_ERRORS.PERMISSION_ERROR;
        }
        
        return { success: false, error: userError, status: result.status, canRetry: false };
      }
      
    } catch (error) {
      lastError = error.message || UPLOAD_ERRORS.NETWORK_ERROR;
      console.error(`[Upload] Attempt ${attempt} failed:`, lastError);
    }
    
    // Wait before retry (except on last attempt)
    if (attempt < maxRetries) {
      onStatus(UPLOAD_ERRORS.UPLOAD_PAUSED);
      console.log(`[Upload] Waiting ${retryDelay}ms before retry...`);
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      onProgress(0); // Reset progress for retry
    }
  }
  
  return { 
    success: false, 
    error: lastError || UPLOAD_ERRORS.NETWORK_ERROR,
    status: lastStatus,
    canRetry: true
  };
}

/**
 * Upload with real progress tracking using XMLHttpRequest
 */
function uploadWithProgress(file, url, headers, onProgress, fieldName = 'file') {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append(fieldName, file);

    // Track upload progress
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(Math.min(percent, 95)); // Cap at 95% until server responds
      }
    });

    // Handle completion
    xhr.addEventListener('load', () => {
      onProgress(100);
      
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ success: true, data, status: xhr.status });
        } catch {
          resolve({ success: true, data: {}, status: xhr.status });
        }
      } else {
        // Parse structured error response
        const errorDetails = parseServerError(xhr.responseText, xhr.status);
        resolve({ 
          success: false, 
          error: errorDetails.message, 
          errorDetails,
          status: xhr.status 
        });
      }
    });

    // Handle errors
    xhr.addEventListener('error', () => {
      resolve({ 
        success: false, 
        error: UPLOAD_ERRORS.NETWORK_ERROR,
        canRetry: true
      });
    });

    xhr.addEventListener('abort', () => {
      resolve({ success: false, error: 'Upload cancelled', canRetry: false });
    });

    xhr.addEventListener('timeout', () => {
      resolve({ success: false, error: UPLOAD_ERRORS.TIMEOUT, canRetry: true });
    });

    // Open and send
    xhr.open('POST', url, true);
    xhr.timeout = 300000; // 5 minute timeout for large files
    
    // Set headers (but not Content-Type - FormData handles that)
    Object.entries(headers).forEach(([key, value]) => {
      if (key.toLowerCase() !== 'content-type') {
        xhr.setRequestHeader(key, value);
      }
    });

    xhr.send(formData);
  });
}

// ===================== UNIFIED IMAGE UPLOAD SERVICE =====================

/**
 * Unified image upload service - use this across the app
 * Handles validation, compression, and upload with retry
 * 
 * @param {Object} options
 * @param {File} options.file - Image file to upload
 * @param {string} options.uploadUrl - API endpoint URL
 * @param {Object} options.headers - Auth headers
 * @param {Function} options.onProgress - Progress callback (0-100)
 * @param {Function} options.onStatus - Status message callback
 * @param {boolean} options.compress - Whether to compress (default: true)
 * @returns {Promise<{ success: boolean, url?: string, data?: any, error?: string, canRetry?: boolean }>}
 */
export async function uploadImage({
  file,
  uploadUrl,
  headers = {},
  onProgress = () => {},
  onStatus = () => {},
  compress = true,
  fieldName = 'file'
}) {
  try {
    // Step 1: Validate
    onStatus('Validating file...');
    const validation = validateImageFile(file);
    if (!validation.valid) {
      return { success: false, error: validation.error, canRetry: false };
    }

    // Step 2: Compress if needed
    let uploadFile = file;
    if (compress) {
      onStatus('Processing image...');
      const processed = await processImageFile(file);
      uploadFile = processed.file;
      if (processed.wasCompressed) {
        console.log(`[UploadImage] Compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(uploadFile.size / 1024 / 1024).toFixed(2)}MB`);
      }
    }

    // Step 3: Upload with retry
    onStatus('Uploading...');
    const result = await uploadFileWithRetry({
      file: uploadFile,
      url: uploadUrl,
      headers,
      onProgress,
      onStatus,
      maxRetries: 3,
      retryDelay: 1500,
      fieldName
    });

    if (result.success) {
      return {
        success: true,
        url: result.data?.url || result.data?.avatar_url || result.data?.banner_url || result.data?.image_url,
        data: result.data,
        canRetry: false
      };
    }

    return result;

  } catch (err) {
    console.error('[UploadImage] Error:', err);
    return {
      success: false,
      error: err.message || UPLOAD_ERRORS.SERVER_ERROR,
      canRetry: true
    };
  }
}

// ===================== UTILITY FUNCTIONS =====================

/**
 * Format file size for display
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Get file extension from filename
 */
export function getFileExtension(filename) {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2).toLowerCase();
}

/**
 * Check if the browser is iOS Safari (needs special handling)
 */
export function isIOSSafari() {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  return isIOS && isSafari;
}

/**
 * Check if file is likely a HEIC image (common on iOS)
 */
export function isHEICFile(file) {
  return file.type === 'image/heic' || 
         file.type === 'image/heif' || 
         file.name.toLowerCase().endsWith('.heic') ||
         file.name.toLowerCase().endsWith('.heif');
}

/**
 * Create an accept string for file inputs
 */
export function createAcceptString(fileTypes) {
  return [...fileTypes.extensions, ...fileTypes.mimeTypes].join(',');
}

/**
 * Create accept string for image inputs
 */
export function createImageAcceptString() {
  return createAcceptString(IMAGE_TYPES);
}

